import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton } from '@repo/common';
import PathfinderQuestionHeader from '../layout/PathfinderQuestionHeader';
import PathfinderBottomToolbar from '../layout/PathfinderBottomToolbar';

import { ReactComponent as BackpackIcon } from '@repo/common/assets/icons/32px/icon_32px_backpack_outline.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

// ─── 날짜 버킷 ────────────────────────────────────────────────────────────────
const TIMELINE_OPTIONS = [
  { label: 'Within 3 months', value: 'WITHIN_3M' },
  { label: 'Within 6 months', value: 'WITHIN_6M' },
  { label: 'Within 1 year', value: 'WITHIN_1Y' },
  { label: 'More than 1 year', value: 'OVER_1Y' },
];

// ─── 드롭다운 ─────────────────────────────────────────────────────────────────
function TimelineDropdown({
  value,
  onChange,
  placeholder = 'Select timeline',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = TIMELINE_OPTIONS.find((o) => o.value === value);

  return (
    <div ref={rootRef} className='relative flex flex-1 flex-col gap-[6px]'>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        typo={false}
        isStatic
        leftalign
        gap='gap-2'
        iconPosition='after'
        icon={
          open ? (
            <ArrowTopIcon className='size-5 shrink-0 text-green-deep02' />
          ) : (
            <ArrowBottomIcon className='size-5 shrink-0 text-text-placeholder' />
          )
        }
        onClick={() => setOpen((p) => !p)}
        textWrapperClass={twMerge(
          'h-auto flex-1 text-left text-base font-medium',
          open || selected ? 'text-green-deep02' : 'text-text-placeholder',
        )}
        className={twMerge(
          'min-w-0 cursor-pointer h-[80px] w-full items-center gap-2 rounded-[15px] border-2 px-6 py-4 transition-colors duration-200',
          open
            ? 'border-surface-green40 bg-surface-lightgray60'
            : 'border-transparent bg-surface-lightgray60',
        )}
      >
        {selected ? selected.label : placeholder}
      </BaseButton>

      {open && (
        <div className='absolute left-0 right-0 top-full z-20 rounded-[15px] border border-grayscale-gray01 bg-grayscale-gray00 p-[8px] shadow-popup-02'>
          {TIMELINE_OPTIONS.map((opt) => {
            const isSel = opt.value === value;
            return (
              <BaseButton
                key={opt.value}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                leftalign
                gap='gap-[2px]'
                icon={
                  <span className='flex h-[18px] w-4 shrink-0 items-center justify-center'>
                    {isSel && <CheckIcon className='size-4 text-text-primary' />}
                  </span>
                }
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                textWrapperClass='h-auto text-sm text-text-primary'
                className='h-[38px] w-full min-w-0 cursor-pointer border-0 bg-transparent items-center rounded-[8px] pl-2 pr-3 py-[10px] text-left transition-colors duration-100 hover:bg-grayscale-gray01'
              >
                {opt.label}
              </BaseButton>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────
function PathfinderTimelineFlow({
  value,
  onChange,
  onComplete,
  onBack,
  config = {},
  options = [],
  progressLabel,
  loading = false,
}) {
  const {
    step1Title = 'Which timeline do you already know?',
    step1Helper = 'Select all that apply',
    step2Title = 'When do you plan to start each program?',
    step2Helper = 'Set an expected start date for each selection',
    step1Options: step1Codes = [],
    noDateOptions = [],
  } = config;

  const step1Options = options
    .filter((o) => step1Codes.includes(o.optionCode))
    .sort(
      (a, b) =>
        step1Codes.indexOf(a.optionCode) - step1Codes.indexOf(b.optionCode),
    )
    .map((o) => ({ id: o.optionCode, title: o.optionLabel }));

  // ── 상태 ──────────────────────────────────────────────────────────────────
  const [subStep, setSubStep] = useState(1);
  const [selections, setSelections] = useState(value?.selections || []);
  const [dates, setDates] = useState(value?.dates || {});

  // ── 완료 여부 ─────────────────────────────────────────────────────────────
  const allDatesComplete =
    selections.length > 0 &&
    selections.every(
      (code) => noDateOptions.includes(code) || dates[code] != null,
    );

  // ── 값 조립 ────────────────────────────────────────────────────────────────
  const buildValue = (
    newSels = selections,
    newDates = dates,
    complete = false,
  ) => ({
    selections: newSels,
    dates: newDates,
    complete,
    experiences: [], // 타임라인 선택은 ExpScope enum과 무관 — C3 섹션 payload에만 저장
  });

  // ── 핸들러 ────────────────────────────────────────────────────────────────
  const toggleSelection = (code) => {
    const next = selections.includes(code)
      ? selections.filter((c) => c !== code)
      : [...selections, code];
    setSelections(next);
    onChange?.(buildValue(next, dates, false));
  };

  const handleDateChange = (code, dateVal) => {
    const nd = { ...dates, [code]: dateVal };
    setDates(nd);
    const complete =
      selections.length > 0 &&
      selections.every((c) => noDateOptions.includes(c) || nd[c] != null);
    onChange?.(buildValue(selections, nd, complete));
  };

  const handleBack = () => {
    if (subStep === 1) onBack?.();
    else setSubStep(1);
  };

  const onNext =
    subStep === 1
      ? () => {
          if (selections.length > 0) setSubStep(2);
        }
      : () => {
          if (allDatesComplete)
            onComplete?.(buildValue(selections, dates, true));
        };

  const nextDisabled =
    loading || (subStep === 1 ? selections.length === 0 : !allDatesComplete);
  const title = subStep === 1 ? step1Title : step2Title;
  const helperText = subStep === 1 ? step1Helper : step2Helper;

  return (
    <div className='flex min-h-[calc(100vh-74px)] flex-col bg-grayscale-gray00'>
      <div className='flex flex-1 items-center justify-center px-20 py-20'>
        <div className='flex w-full max-w-[868px] flex-1 flex-col items-start justify-center self-stretch overflow-visible'>
          <PathfinderQuestionHeader
            progressLabel={progressLabel}
            title={title}
            helperText={helperText}
          />

          {/* Step 1: 2-column grid multi-select */}
          {subStep === 1 && (
            <div className='grid w-full grid-cols-2 gap-4'>
              {step1Options.map((opt) => {
                const isSel = selections.includes(opt.id);
                return (
                  <BaseButton
                    key={opt.id}
                    type='text'
                    size='m'
                    htmlType='button'
                    typo={false}
                    isStatic
                    leftalign
                    gap='gap-2'
                    icon={
                      <div className='flex shrink-0 items-center px-2'>
                        <BackpackIcon
                          className={twMerge(
                            'size-8',
                            isSel
                              ? 'text-green-deep02'
                              : 'text-text-placeholder',
                          )}
                        />
                      </div>
                    }
                    onClick={() => toggleSelection(opt.id)}
                    textWrapperClass={twMerge(
                      'h-auto flex-1 text-left text-base font-medium',
                      isSel ? 'text-green-deep02' : 'text-text-placeholder',
                    )}
                    className={twMerge(
                      'h-[76px] min-w-0 cursor-pointer items-center gap-2 rounded-[15px] border-2 p-4 transition-colors duration-200',
                      isSel
                        ? 'border-surface-green40 bg-green-light01'
                        : 'border-transparent bg-surface-lightgray60',
                    )}
                  >
                    {opt.title}
                  </BaseButton>
                );
              })}
            </div>
          )}

          {/* Step 2: pressed 카드 + 날짜 드롭다운 */}
          {subStep === 2 && (
            <div className='flex w-full flex-col gap-4'>
              {selections.map((code) => {
                const opt = step1Options.find((o) => o.id === code);
                if (!opt) return null;
                const needsDate = !noDateOptions.includes(code);
                return (
                  <div key={code} className='flex items-center gap-4'>
                    {/* pressed 상태 고정 (클릭 불가) */}
                    <div className='flex h-[76px] flex-1 items-center gap-2 rounded-[15px] border-2 border-surface-green40 bg-green-light01 p-4'>
                      <div className='flex shrink-0 items-center px-2'>
                        <BackpackIcon className='size-8 text-green-deep02' />
                      </div>
                      <span className='flex-1 text-left text-base font-medium text-green-deep02'>
                        {opt.title}
                      </span>
                    </div>

                    {needsDate ? (
                      <TimelineDropdown
                        value={dates[code] ?? null}
                        onChange={(v) => handleDateChange(code, v)}
                      />
                    ) : (
                      <div className='flex h-[80px] flex-1 items-center rounded-[15px] bg-surface-lightgray60 px-6 py-4 opacity-40'>
                        <span className='text-base font-medium text-text-placeholder'>
                          No date needed
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PathfinderBottomToolbar
        onBack={handleBack}
        onNext={onNext}
        nextLabel={subStep === 2 ? 'Save & Continue' : 'Next'}
        nextDisabled={nextDisabled}
      />
    </div>
  );
}

export default PathfinderTimelineFlow;
